/**
 * upnpStatePoller — Ground-truth UPnP renderer state + position polling.
 *
 * Polls AVTransport:GetTransportInfo + GetPositionInfo on the active UPnP
 * renderer. Corrects Zustand position from the real renderer position and
 * detects ghost playback (local says playing, renderer says stopped).
 *
 * When drift is detected and sustained (3 consecutive polls), triggers
 * auto-recovery via QueueController to re-send the current track.
 */

import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { QueueController } from './QueueController';
import { evaluateDeviceReportedPosition } from './positionReconcile';

const POLL_INTERVAL_MS = 5000;
const FETCH_TIMEOUT_MS = 2500;
const GHOST_THRESHOLD = 2; // consecutive drift polls before auto-recovery
// Hi-res FLAC renderers can buffer for 10-15 s before reporting PLAYING.
// Suppress ghost detection for this long after a play dispatch so a renderer
// that is still in STOPPED/TRANSITIONING state during initial buffering is
// not mistakenly recovered, which would restart the track from the beginning.
const GHOST_GRACE_MS = 20000;

let _pollTimer: ReturnType<typeof setInterval> | null = null;
let _activeZoneId: string | null = null;
let _consecutiveDriftCount = 0;
let _recovering = false;
let _playDispatchTime = 0;

const SOAP_GET_TRANSPORT_INFO = `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"
  s:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">
  <s:Body>
    <u:GetTransportInfo xmlns:u="urn:schemas-upnp-org:service:AVTransport:1">
      <InstanceID>0</InstanceID>
    </u:GetTransportInfo>
  </s:Body>
</s:Envelope>`;

const SOAP_GET_POSITION_INFO = `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"
  s:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">
  <s:Body>
    <u:GetPositionInfo xmlns:u="urn:schemas-upnp-org:service:AVTransport:1">
      <InstanceID>0</InstanceID>
    </u:GetPositionInfo>
  </s:Body>
</s:Envelope>`;

function extractXmlTag(xml: string, tag: string): string {
  try {
    const re = new RegExp(`<${tag}>([^<]*)</${tag}>`, 'i');
    const m = xml.match(re);
    return m ? m[1] : '';
  } catch {
    return '';
  }
}

function parseUpnpTime(timeStr: string): number {
  if (!timeStr || timeStr === 'NOT_IMPLEMENTED') return 0;
  const parts = timeStr.split(':');
  if (parts.length !== 3) return 0;
  const h = parseInt(parts[0]) || 0;
  const m = parseInt(parts[1]) || 0;
  const s = parseFloat(parts[2]) || 0;
  return h * 3600 + m * 60 + s;
}

function buildControlUrl(zone: any): string | null {
  const ip = zone?.ip || zone?.host;
  const port = zone?.port || 1400;
  if (!ip) return null;
  const avtPath = zone?.avt_url || zone?.avtUrl || '/MediaRenderer/AVTransport/Control';
  if (avtPath.startsWith('http')) return avtPath;
  return `http://${ip}:${port}${avtPath}`;
}

async function fetchWithTimeout(url: string, opts: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function pollOnce(controlUrl: string, zoneName: string): Promise<void> {
  if (_recovering) return;

  const store = useMaestroStore.getState();
  const localState = store.playbackState;

  try {
    const [transportResp, positionResp] = await Promise.all([
      fetchWithTimeout(controlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          'SOAPAction': '"urn:schemas-upnp-org:service:AVTransport:1#GetTransportInfo"',
        },
        body: SOAP_GET_TRANSPORT_INFO,
      }, FETCH_TIMEOUT_MS),
      fetchWithTimeout(controlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml; charset=utf-8',
          'SOAPAction': '"urn:schemas-upnp-org:service:AVTransport:1#GetPositionInfo"',
        },
        body: SOAP_GET_POSITION_INFO,
      }, FETCH_TIMEOUT_MS),
    ]);

    const transportXml = await transportResp.text();
    const positionXml = await positionResp.text();

    const remoteState = extractXmlTag(transportXml, 'CurrentTransportState');
    const relTime = extractXmlTag(positionXml, 'RelTime');
    const trackDuration = extractXmlTag(positionXml, 'TrackDuration');

    const remotePosSec = parseUpnpTime(relTime);
    const remoteDurSec = parseUpnpTime(trackDuration);

    // Correct position from renderer ground truth
    if (remoteState === 'PLAYING' && remotePosSec >= 0) {
      const localPos = store.position || 0;
      const drift = Math.abs(localPos - remotePosSec);

      // Correct if drifted more than 2 seconds
      if (drift > 2) {
        const zoneId = store.discovery?.activeZone?.id || 'unknown';
        const positionDecision = evaluateDeviceReportedPosition({
          reportedPositionSecs: remotePosSec,
          durationSecs: remoteDurSec || store.duration,
          rendererId: String(zoneId),
        });
        if (!positionDecision.accept) {
          diagnosticLog('info', 'playback', '[playback] device_position_stale_rejected', positionDecision.staleLog);
        } else {
          console.log(`[UPnPPoll] Correcting position: local=${Math.floor(localPos)}s → renderer=${Math.floor(remotePosSec)}s (drift=${Math.floor(drift)}s)`);
          store.updateTime(remotePosSec, remoteDurSec || store.duration);
        }
      }

      _consecutiveDriftCount = 0;
    }

    // Sync paused state from renderer ground truth (honest UI — no wall-clock advance)
    if (localState === 'playing' && remoteState === 'PAUSED_PLAYBACK') {
      console.log(`[UPnPPoll] Syncing paused — local=playing, ${zoneName}=PAUSED_PLAYBACK`);
      store.setPlaybackState('paused');
      _consecutiveDriftCount = 0;
    } else if (localState === 'paused' && remoteState === 'PLAYING') {
      console.log(`[UPnPPoll] Syncing playing — local=paused, ${zoneName}=PLAYING`);
      store.setPlaybackState('playing');
      _consecutiveDriftCount = 0;
    }

    // Detect ghost playback: we think we're playing but the renderer disagrees
    const stoppedStates = ['STOPPED', 'NO_MEDIA_PRESENT'];
    if (localState === 'playing' && stoppedStates.includes(remoteState)) {
      // Respect the grace period — during initial buffering of hi-res FLAC the
      // renderer can stay STOPPED for 10-15 s before reporting PLAYING.  If we
      // count those polls toward the ghost threshold the recovery fires at ~9 s
      // and re-dispatches SetAVTransportURI, restarting the track from byte 0.
      const inGrace = _playDispatchTime > 0 && (Date.now() - _playDispatchTime) < GHOST_GRACE_MS;
      if (!inGrace) {
        _consecutiveDriftCount++;
        console.warn(
          `[UPnPPoll] ⚠️ Ghost playback — local=playing, ${zoneName}=${remoteState} (${_consecutiveDriftCount}/${GHOST_THRESHOLD + 1})`,
        );

        if (_consecutiveDriftCount > GHOST_THRESHOLD) {
          console.error('[UPnPPoll] Ghost playback confirmed — attempting auto-recovery');
          _consecutiveDriftCount = 0;
          recoverGhostPlayback();
        }
      } else {
        console.log(
          `[UPnPPoll] STOPPED during grace period (${Math.round((Date.now() - _playDispatchTime) / 1000)}s / ${GHOST_GRACE_MS / 1000}s) — waiting for renderer to buffer`,
        );
      }
    } else if (localState === 'playing' && remoteState === 'PLAYING') {
      _consecutiveDriftCount = 0;
    }

    // Detect track-ended: renderer stopped but we haven't advanced yet
    if (localState === 'playing' && remoteState === 'STOPPED' && remotePosSec === 0 && _consecutiveDriftCount <= 1) {
      // Could be natural track end — let QueueController's normal advance handle it
    }

  } catch (err: any) {
    const msg = String(err?.message || err || '');
    if (msg.includes('abort') || msg.includes('timeout')) {
      // Renderer unreachable — don't correct
    } else if (msg.includes('CORS') || msg.includes('NetworkError') || msg.includes('Failed to fetch') || msg.includes('Load failed')) {
      // Can't reach renderer from web context — expected on some setups
    } else {
      console.log('[UPnPPoll] poll error:', msg);
    }
  }
}

async function recoverGhostPlayback(): Promise<void> {
  _recovering = true;
  try {
    const store = useMaestroStore.getState();
    const currentTrack = store.currentTrack;
    if (!currentTrack) {
      store.setPlaybackState('stopped');
      return;
    }

    console.log('[UPnPPoll] Recovery: re-playing current track from beginning');
    store.setPlaybackState('loading');

    // Re-play current track via QueueController
    const queueIndex = store.queueIndex;
    await QueueController.playQueueTrack(queueIndex);
  } catch (err) {
    console.error('[UPnPPoll] Recovery failed:', err);
    useMaestroStore.getState().setPlaybackState('stopped');
  } finally {
    setTimeout(() => { _recovering = false; }, 5000);
  }
}

/** Call this immediately after dispatching a play command so the ghost detector waits. */
export function notifyPlayDispatched(): void {
  _playDispatchTime = Date.now();
  _consecutiveDriftCount = 0;
}

export function startUpnpStatePoll(zone: any): void {
  stopUpnpStatePoll();

  const controlUrl = buildControlUrl(zone);
  const zoneName = zone?.name || zone?.id || 'unknown';
  _activeZoneId = zone?.id || null;
  _consecutiveDriftCount = 0;

  if (!controlUrl) {
    console.log('[UPnPPoll] Cannot start — no IP/port for zone:', zoneName);
    return;
  }

  console.log('[UPnPPoll] Starting poll for', zoneName, 'at', controlUrl, '(every 3s)');

  pollOnce(controlUrl, zoneName);

  _pollTimer = setInterval(() => {
    const s = useMaestroStore.getState();
    if (s.playbackState !== 'playing' && s.playbackState !== 'loading') {
      stopUpnpStatePoll();
      return;
    }
    pollOnce(controlUrl, zoneName);
  }, POLL_INTERVAL_MS);
}

export function stopUpnpStatePoll(): void {
  if (_pollTimer) {
    clearInterval(_pollTimer);
    _pollTimer = null;
  }
  _activeZoneId = null;
  _consecutiveDriftCount = 0;
  _playDispatchTime = 0;
}

export function getActivePollerZoneId(): string | null {
  return _activeZoneId;
}
